// SettingsFaq.jsx
import React from 'react';
import { useLoaderData } from 'react-router';

import UniversalContainer from '../component/UniversalContainer.jsx';
import MetaTagsHelmet from '../component/MetaTagsHelmet.jsx';
import prepareFAQPageMeta from '../resource/prepareFAQPageMeta.js';
import generateCanonicalAndAlternates from '../resource/generateCanonicalAndAlternates.js';
import { createInitialStoreWithRequest } from '../createInitialStore.js';
import getHttpProxyHeaders from '../resource/getHttpProxyHeaders.js';
import fetchFaqArticles from '../action/fetchFaqArticles.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import { FAQ_SUPPORTED_LANGUAGES } from '../RemoteConfigKeys.js';
import { getCurrentFaqLanguage } from '../resource/getCurrentLanguage.js';
import { EMPTY_ARRAY } from '../resource/defaults.js';
import getMetaTagsForHelmet from '../resource/getMetaTagsForHelmet.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { getI18nInstance } from '../resource/i18n.js';

/**
 * Loader function for FAQ page SEO
 * @param {Object} context - Loader context
 * @param {Object} context.request - Request object
 * @param {Object} context.params - Route params
 * @returns {Promise<Object>} Object with meta tags
 */
export const loader = async ({ request, params }) => {
  try {
    const httpProxyHeaders = getHttpProxyHeaders({ request });

    // Create store with request
    const { store } = await createInitialStoreWithRequest({
      params,
      request,
    });

    // Get state and language
    const state = store.getState();
    const faqSupportedLanguages = getRemoteConfigData(
      state,
      FAQ_SUPPORTED_LANGUAGES
    );

    // Get i18n instance
    const { i18n } = getI18nInstance();
    const i18nLanguage = i18n.language || 'zh-TW';
    const language = getCurrentFaqLanguage({
      i18nLanguage,
      supportedLanguages: faqSupportedLanguages,
    });

    // Fetch FAQ articles
    await store.dispatch(
      fetchFaqArticles({
        page: 1,
        limit: 20,
        language,
        httpProxyHeaders,
      })
    );

    // Get final state after fetching
    const finalState = store.getState();
    const hostname = request?.headers?.get('host') || 'swag.live';

    // Get available languages for alternates
    const availableLanguages = getRemoteConfigData(
      finalState,
      'SUPPORTED_LANGUAGES'
    );

    // Generate canonical and alternates
    const canonicalPath = '/settings/faq';
    const { canonical, alternates } = generateCanonicalAndAlternates({
      canonicalPath,
      availableLanguages,
    });

    // Prepare meta tags with JSON-LD
    const meta = await prepareFAQPageMeta({
      state: finalState,
      language,
      hostname,
      includeJsonLd: true,
      canonical,
      alternates,
      i18n,
      supportedLanguages: availableLanguages,
    });

    return { meta, params };
  } catch (error) {
    // Return basic meta tags on error
    const hostname = request?.headers?.get('host');

    // Get i18n instance
    const { i18n } = getI18nInstance();

    const title = i18n.t('seo_faq_title', {
      ns: TranslationNamespace.SEO,
    });
    const description = i18n.t('seo_faq_description', {
      ns: TranslationNamespace.SEO,
    });

    const meta = getMetaTagsForHelmet({
      title,
      description,
      hostname,
      ogType: 'website',
      twitterCard: 'summary',
    });

    return {
      meta,
      params,
      error,
    };
  }
};

const SettingsFaqRoute = () => {
  const { meta = EMPTY_ARRAY } = useLoaderData();

  return (
    <>
      <MetaTagsHelmet metaTags={meta} />
      <UniversalContainer filename="Faq" />
    </>
  );
};

export default SettingsFaqRoute;
